import * as z from 'zod'
import { partyZ, selectedExtrasZ } from '../schema'

const addressZ = z.object({
  city: z.string(),
  country: z.string(),
  postCode: z.string(),
  noPostCode: z.string().optional(),
  street: z.string(),
})

const internationalPhoneZ = z.object({
  countryDialingCode: z.string(),
  number: z.string(),
  countryDialingNumber: z.string().optional(),
})

const contactDetailsZ = z.object({
  address: addressZ,
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  phone: internationalPhoneZ,
  sendTextMessage: z.boolean(),
  password: z.string().optional(),
})

const otherDetailsZ = z.object({
  carRegistration: z.string().nullable().optional(),
  partyNames: z.string().nullable().optional(),
  groupName: z.string().nullable().optional(),
  specialRequests: z.string().nullable().optional(),
  unitType: z.string().nullable().optional(),
  unitWidth: z.string().nullable().optional(),
  unitDepth: z.string().nullable().optional(),
  estimatedTimeOfArrival: z.string().nullable().optional(),
})

const dateRange = z.object({
  start: z.string(),
  end: z.string(),
})

const stayDetailsZ = z.object({
  chargeTypeId: z.string(),
  pitchtypeId: z.string(),
  party: partyZ,
  range: dateRange,
})

const trackingDetailsZ = z.object({
  token: z.string(),
})

const paymentDetailsBaseZ = z.object({
  takePaymentImmediately: z.boolean(),
  stripeAccount: z.string().nullable().optional(),
})

const paymentDetailsZ = z.union([
  paymentDetailsBaseZ.extend({
    method: z.literal('card'),
    cardId: z.string(),
  }),
  paymentDetailsBaseZ.extend({
    method: z.literal('apple-pay'),
    paymentMethodId: z.string(),
    radarSessionId: z.string().nullable().optional(),
  }),
  paymentDetailsBaseZ.extend({
    method: z.literal('google-pay'),
    paymentMethodId: z.string(),
    radarSessionId: z.string().nullable().optional(),
  }),
  paymentDetailsBaseZ.extend({
    method: z.literal('new-card'),
    paymentMethodId: z.string(),
    saveCard: z.boolean(),
    radarSessionId: z.string().nullable().optional(),
  }),
  paymentDetailsBaseZ.extend({
    method: z.literal('paypal'),
  }),
  paymentDetailsBaseZ.extend({
    method: z.literal('not-selected'),
  }),
  paymentDetailsBaseZ.extend({
    method: z.literal('idealBank'),
    bank: z.string(),
    paymentMethodId: z.string(),
    radarSessionId: z.string().nullable().optional(),
  }),
])

export const bookingSubmitRequestZ = z.object({
  contactDetails: contactDetailsZ,
  langCode: z.string(),
  otherDetails: otherDetailsZ,
  paymentDetails: paymentDetailsZ,
  selectedExtras: selectedExtrasZ,
  slug: z.string(),
  stayDetails: stayDetailsZ,
  trackingDetails: trackingDetailsZ,
  bookingMeta: z.string().optional(),
})

export type BookingSubmitRequest = z.infer<typeof bookingSubmitRequestZ>
export type ContactDetails = BookingSubmitRequest['contactDetails']
export type OtherDetails = BookingSubmitRequest['otherDetails']
export type PaymentDetails = BookingSubmitRequest['paymentDetails']
export type StayDetails = BookingSubmitRequest['stayDetails']

export const bookingSubmitResponseZ = z.object({
  taskId: z.string(),
  bookingId: z.string(),
})

export type BookingSubmitResponse = z.infer<typeof bookingSubmitResponseZ>

export const bookingSubmitEndpoint = z
  .function()
  .args(bookingSubmitRequestZ)
  .returns(z.promise(bookingSubmitResponseZ))
